<template>
  <div class="recruitment-dashboard">
    <div class="back-button">
      <el-button @click="navigateBack">
        返回主屏幕
      </el-button>
    </div>
    
    <div class="screen-title">
      社区招聘
    </div>

    <!-- 主要内容区域 -->
    <main class="dashboard-content">
      <!-- 关键指标卡片 -->
      <el-row
        :gutter="20"
        class="metrics-row"
      >
        <el-col :span="6">
          <el-card class="metric-card">
            <div class="metric-content">
              <div class="metric-info">
                <p class="metric-label">
                  总招聘岗位数
                </p>
                <h3 class="metric-value">
                  {{ totalJobs }}
                </h3>
              </div>
              <div class="metric-icon">
                <i class="el-icon-briefcase" />
                <div class="progress-ring">
                  <div class="progress-text">
                    +{{ jobsGrowth }}%
                  </div>
                </div>
              </div>
            </div>
            <div class="metric-trend positive">
              <i class="el-icon-top" />
              <span>{{ jobsGrowth }}%</span>
              <span class="trend-label">较上月</span>
            </div>
          </el-card>
        </el-col>
        
        <el-col :span="6">
          <el-card class="metric-card">
            <div class="metric-content">
              <div class="metric-info">
                <p class="metric-label">
                  活跃招聘企业
                </p>
                <h3 class="metric-value">
                  {{ activeCompanies }}
                </h3>
              </div>
              <div class="metric-icon">
                <i class="el-icon-office-building" />
                <div class="pulse-indicator">
                  <div class="pulse-dot" />
                  <div class="pulse-wave" />
                </div>
              </div>
            </div>
            <div class="metric-trend positive">
              <i class="el-icon-top" />
              <span>{{ companiesGrowth }}%</span>
              <span class="trend-label">较上月</span>
            </div>
          </el-card>
        </el-col>
        
        <el-col :span="6">
          <el-card class="metric-card">
            <div class="metric-content">
              <div class="metric-info">
                <p class="metric-label">
                  平均薪资(元/月)
                </p>
                <h3 class="metric-value">
                  {{ avgSalary }}
                </h3>
              </div>
              <div class="metric-icon">
                <i class="el-icon-money" />
                <div class="trend-arrow">
                  <i class="el-icon-top" />
                  <span class="trend-value">+{{ salaryGrowth }}%</span>
                </div>
              </div>
            </div>
            <div class="metric-trend positive">
              <i class="el-icon-top" />
              <span>{{ salaryGrowth }}%</span>
              <span class="trend-label">较上月</span>
            </div>
          </el-card>
        </el-col>
        
        <el-col :span="6">
          <el-card class="metric-card">
            <div class="metric-content">
              <div class="metric-info">
                <p class="metric-label">
                  求职人数
                </p>
                <h3 class="metric-value">
                  {{ jobSeekers }}
                </h3>
              </div>
              <div class="metric-icon">
                <i class="el-icon-user" />
                <div class="data-counter">
                  <div class="counter-value">
                    {{ seekersGrowth }}%
                  </div>
                  <div class="counter-label">
                    变化
                  </div>
                </div>
              </div>
            </div>
            <div class="metric-trend negative">
              <i class="el-icon-bottom" />
              <span>{{ seekersGrowth }}%</span>
              <span class="trend-label">较上月</span>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <!-- 岗位信息展示区域 -->
      <el-row
        :gutter="20"
        class="job-info-row"
      >
        <el-col :span="16">
          <el-card class="chart-card">
            <div class="card-header">
              <h3>行业招聘岗位分布</h3>
              <div class="time-filter">
                <el-button
                  size="mini"
                  :type="timeFilter === 'month' ? 'primary' : ''"
                  @click="setTimeFilter('month')"
                >
                  月度
                </el-button>
                <el-button
                  size="mini"
                  :type="timeFilter === 'quarter' ? 'primary' : ''"
                  @click="setTimeFilter('quarter')"
                >
                  季度
                </el-button>
                <el-button
                  size="mini"
                  :type="timeFilter === 'year' ? 'primary' : ''"
                  @click="setTimeFilter('year')"
                >
                  年度
                </el-button>
              </div>
            </div>
            <div
              ref="industryChart"
              class="chart-container industry-chart-container"
            />
          </el-card>
        </el-col>
        
        <el-col :span="8">
          <el-card class="chart-card">
            <div class="card-header">
              <h3>薪资水平分布</h3>
            </div>
            <div
              ref="salaryChart"
              class="chart-container salary-chart-container"
            />
          </el-card>
        </el-col>
      </el-row>

      <!-- 招聘趋势和热门岗位 -->
      <el-row
        :gutter="20"
        class="trend-row"
      >
        <el-col :span="16">
          <el-card class="chart-card">
            <div class="card-header">
              <h3>近6个月招聘趋势</h3>
            </div>
            <div
              ref="trendChart"
              class="chart-container"
            />
          </el-card>
        </el-col>
        
        <el-col :span="8">
          <el-card class="job-list-card">
            <div class="card-header">
              <h3>热门岗位信息</h3>
              <el-button
                type="text"
                size="mini"
                @click="refreshJobList"
              >
                <i class="el-icon-refresh" />
              </el-button>
            </div>
            <div class="job-list">
              <div
                v-for="(job, index) in hotJobs"
                :key="index"
                class="job-item"
              >
                <div class="job-name">
                  {{ job.name }}
                </div>
                <div class="job-stats">
                  <span class="recruit-count">招聘: {{ job.recruitCount }}</span>
                  <span class="applicant-count">申请: {{ job.applicantCount }}</span>
                </div>
                <div class="job-salary">
                  {{ job.salary }}
                </div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </main>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import * as echarts from 'echarts'
// import screenAdapter from '@/mixins/screenAdapter'

export default {
  name: 'CommunityRecruitmentDashboard',
  setup() {
    // 数据状态
    const updateTime = ref('2023-10-15 10:30:00')
    const totalJobs = ref('12,845')
    const activeCompanies = ref('1,248')
    const avgSalary = ref('8,642')
    const jobSeekers = ref('28,753')
    const jobsGrowth = ref('8.2')
    const companiesGrowth = ref('5.3')
    const salaryGrowth = ref('3.1')
    const seekersGrowth = ref('-2.4')
    const timeFilter = ref('month')
    const trendFilter = ref('jobs') // 未使用的变量，考虑删除或添加相应功能
    
    // 图表引用
    const industryChart = ref(null)
    const salaryChart = ref(null)
    const trendChart = ref(null)
    
    // 热门岗位数据
    const hotJobs = ref([
      { name: '前端开发工程师', recruitCount: 1250, applicantCount: 3580, salary: '8K-15K' },
      { name: 'Java开发工程师', recruitCount: 980, applicantCount: 2850, salary: '10K-20K' },
      { name: '产品经理', recruitCount: 750, applicantCount: 2100, salary: '12K-25K' }
    ])
    
    // 行业招聘数据
    const industryData = ref({
      month: [
        { value: 3200, name: '互联网' },
        { value: 2800, name: '金融' },
        { value: 1500, name: '教育' },
        { value: 1200, name: '医疗' },
        { value: 1800, name: '制造' },
        { value: 900, name: '零售' }
      ],
      quarter: [
        { value: 8500, name: '互联网' },
        { value: 7200, name: '金融' },
        { value: 6800, name: '教育' },
        { value: 4200, name: '医疗' },
        { value: 3900, name: '制造' },
        { value: 2400, name: '零售' }
      ],
      year: [
        { value: 32000, name: '互联网' },
        { value: 28000, name: '金融' },
        { value: 22000, name: '教育' },
        { value: 18000, name: '医疗' },
        { value: 15000, name: '制造' },
        { value: 8000, name: '零售' }
      ]
    })
    
    // 初始化图表
    const initCharts = () => {
      nextTick(() => {
        // 行业招聘岗位分布图表
        if (industryChart.value) {
          const instance = echarts.init(industryChart.value)
          const currentData = industryData.value[timeFilter.value]
          instance.setOption({
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
            legend: { data: currentData.map(item => item.name), textStyle: { color: '#fff' } },
            grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
            xAxis: { 
              type: 'value',
              axisLabel: { color: '#606266' }
            },
            yAxis: { 
              type: 'category',
              data: currentData.map(item => item.name),
              axisLabel: { color: '#606266' }
            },
            series: [{
              name: '招聘岗位',
              type: 'bar',
              data: currentData.map(item => item.value),
              itemStyle: {
                color: function(params) {
                  const colorList = ['#003366', '#0066cc', '#3399ff', '#66b3ff', '#99ccff', '#cce6ff']
                  return colorList[params.dataIndex]
                }
              }
            }]
          })
          const handleResize = () => instance.resize()
          window.addEventListener('resize', handleResize)
          // 在组件卸载时移除事件监听器，防止内存泄漏
          onBeforeUnmount(() => {
            window.removeEventListener('resize', handleResize)
          })
        }
        
        // 薪资水平分布图表
        if (salaryChart.value) {
          const instance = echarts.init(salaryChart.value)
          instance.setOption({
            tooltip: { trigger: 'item' },
            series: [{
              name: '薪资分布',
              type: 'pie',
              radius: ['40%', '70%'],
              data: [
                { value: 35, name: '5K以下' },
                { value: 25, name: '5K-8K' },
                { value: 20, name: '8K-12K' },
                { value: 15, name: '12K-20K' },
                { value: 5, name: '20K以上' }
              ],
              itemStyle: {
                color: function(params) {
                  const colorList = ['#1E3A8A', '#2563EB', '#60A5FA', '#93BBFC', '#C3D9FE']
                  return colorList[params.dataIndex]
                }
              }
            }]
          })
          const handleSalaryResize = () => instance.resize()
          window.addEventListener('resize', handleSalaryResize)
          // 在组件卸载时移除事件监听器，防止内存泄漏
          onBeforeUnmount(() => {
            window.removeEventListener('resize', handleSalaryResize)
          })
        }
        
        // 招聘趋势图表
        if (trendChart.value) {
          const instance = echarts.init(trendChart.value)
          instance.setOption({
            tooltip: { trigger: 'axis' },
            legend: { data: ['岗位数', '申请数'], textStyle: { color: '#ffffff' } },
            grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
            xAxis: { 
              type: 'category', 
              data: ['5月', '6月', '7月', '8月', '9月', '10月'],
              axisLabel: { color: '#606266' }
            },
            yAxis: { 
              type: 'value', 
              axisLabel: { color: '#606266' }
            },
            series: [
              {
                name: '岗位数',
                type: 'line',
                data: [8200, 9500, 11000, 11800, 12500, 12845],
                itemStyle: { color: '#409EFF' }
              },
              {
                name: '申请数',
                type: 'line',
                data: [15000, 18000, 22000, 25000, 27000, 28753],
                itemStyle: { color: '#67C23A' }
              }
            ]
          })
          const handleTrendResize = () => instance.resize()
          window.addEventListener('resize', handleTrendResize)
          // 在组件卸载时移除事件监听器，防止内存泄漏
          onBeforeUnmount(() => {
            window.removeEventListener('resize', handleTrendResize)
          })
        }
      })
    }
    
    // 刷新数据
    const refreshData = () => {
      updateTime.value = new Date().toLocaleString('zh-CN')
      // 模拟数据更新
      totalJobs.value = (Math.floor(Math.random() * 2000) + 12000).toLocaleString()
      activeCompanies.value = (Math.floor(Math.random() * 200) + 1200).toLocaleString()
      avgSalary.value = (Math.floor(Math.random() * 1000) + 8000).toLocaleString()
      jobSeekers.value = (Math.floor(Math.random() * 3000) + 27000).toLocaleString()
      
      // 重新初始化图表
      initCharts()
    }
    
    // 刷新岗位列表
    const refreshJobList = () => {
      // 模拟岗位数据更新
      hotJobs.value = hotJobs.value.map(job => ({
        ...job,
        recruitCount: Math.floor(Math.random() * 500) + 1000,
        applicantCount: Math.floor(Math.random() * 2000) + 2000
      }))
    }
    
    // 设置时间过滤器
    const setTimeFilter = (filter) => {
      timeFilter.value = filter
      initCharts()
    }
    
    // 设置趋势过滤器
    const setTrendFilter = (filter) => {
      trendFilter.value = filter
      initCharts()
    } // 未使用的函数，考虑删除或添加相应功能
    
    // 返回上一页
    const router = useRouter()
    const navigateBack = () => {
      router.back()
    }
    
    onMounted(() => {
      initCharts()
    })
    
    return {
      updateTime,
      totalJobs,
      activeCompanies,
      avgSalary,
      jobSeekers,
      jobsGrowth,
      companiesGrowth,
      salaryGrowth,
      seekersGrowth,
      timeFilter,
      trendFilter,
      hotJobs,
      industryData,
      industryChart,
      salaryChart,
      trendChart,
      refreshData,
      refreshJobList,
      setTimeFilter,
      setTrendFilter,
      navigateBack
    }
  }
}
</script>

<style scoped>
.screen-title {
  font-size: 32px;
  font-weight: bold;
  color: white;
  letter-spacing: 2px;
  text-align: center;
  margin-bottom: 20px;
}

.back-button {
  margin-bottom: 20px;
  text-align: left;
}

.back-button .el-button {
  background: linear-gradient(135deg, #122a44, #0a1a2a);
  border: none;
  color: white;
}

.recruitment-dashboard {
  min-height: 100vh;
  padding: 20px;
  background-color: #000000;
  color: white;
  position: relative;
  overflow: hidden;
}

.recruitment-dashboard::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.recruitment-dashboard::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(22, 93, 255, 0.3) 0%, transparent 70%);
  filter: blur(200px);
  pointer-events: none;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  padding: 15px 25px;
  border-radius: 10px;
  margin-bottom: 20px;
  backdrop-filter: blur(10px);
}

.title-container {
  display: flex;
  align-items: center;
}

.title-container i {
  font-size: 24px;
  margin-right: 10px;
  color: white;
}

.title-container h1 {
  font-size: 24px;
  font-weight: bold;
  margin: 0;
  background: linear-gradient(45deg, #409EFF, #67C23A);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 20px;
}

.update-time {
  font-size: 14px;
  color: white;
}

.dashboard-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.metrics-row {
  margin-bottom: 0;
}

.metric-card {
  border: none;
  border-radius: 4px;
  background: linear-gradient(135deg, #122a44, #0a1a2a);
  background-image: 
    radial-gradient(circle at 30% 30%, rgba(24, 144, 255, 0.15) 0%, transparent 50%),
    radial-gradient(circle at 70% 70%, rgba(24, 144, 255, 0.1) 0%, transparent 40%);
  box-shadow: 0 0 20px rgba(24, 144, 255, 0.4), inset 0 0 25px rgba(24, 144, 255, 0.15);
  color: #fff;
  transition: transform 0.3s ease;
  padding: 15px;
  box-sizing: border-box;
}

.metric-card:hover {
  transform: translateY(-5px);
}

.metric-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
}

.metric-label {
  font-size: 14px;
  color: white;
  margin: 0;
}

.metric-value {
  font-size: 28px;
  font-weight: bold;
  margin: 5px 0 0 0;
}

.metric-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(64, 158, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #409EFF;
  font-size: 18px;
  position: relative;
  overflow: hidden;
}

/* 进度环样式 */
.progress-ring {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.progress-circle {
  animation: progressRotate 2s ease-in-out infinite;
}

.progress-text {
  position: absolute;
  font-size: 8px;
  font-weight: bold;
  color: #409EFF;
  text-align: center;
}

/* 脉冲指示器样式 */
.pulse-indicator {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pulse-dot {
  width: 6px;
  height: 6px;
  background: #409EFF;
  border-radius: 50%;
  position: absolute;
  z-index: 2;
}

.pulse-wave {
  width: 6px;
  height: 6px;
  background: rgba(64, 158, 255, 0.6);
  border-radius: 50%;
  position: absolute;
  animation: pulseWave 2s ease-in-out infinite;
}

/* 趋势箭头样式 */
.trend-arrow {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: white;
}

.trend-value {
  font-size: 8px;
  font-weight: bold;
  margin-top: 2px;
}

/* 数据计数器样式 */
.data-counter {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  color: white;
}

.counter-value {
  font-weight: bold;
  font-size: 10px;
}

.counter-label {
  font-size: 6px;
  opacity: 0.8;
  margin-top: 1px;
}

/* 动画定义 */
@keyframes progressRotate {
  0% { stroke-dashoffset: 113; }
  50% { stroke-dashoffset: 85; }
  100% { stroke-dashoffset: 113; }
}

@keyframes pulseWave {
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  50% {
    transform: scale(3);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 0.6;
  }
}

.metric-trend {
  display: flex;
  align-items: center;
  font-size: 12px;
  gap: 5px;
}

.metric-trend.positive {
  color: #67C23A;
}

.metric-trend.negative {
  color: #F56C6C;
}

.trend-label {
  color: white;
}

.chart-card, .job-list-card {
  border: none;
  border-radius: 4px;
  background: linear-gradient(135deg, #122a44, #0a1a2a);
  background-image: 
    radial-gradient(circle at 30% 30%, rgba(24, 144, 255, 0.15) 0%, transparent 50%),
    radial-gradient(circle at 70% 70%, rgba(24, 144, 255, 0.1) 0%, transparent 40%);
  box-shadow: 0 0 20px rgba(24, 144, 255, 0.4), inset 0 0 25px rgba(24, 144, 255, 0.15);
  color: #fff;
  padding: 15px;
  box-sizing: border-box;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.card-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: bold;
}

.chart-container {
  height: 340px;
}

.industry-chart-container {
  height: 290px;
}

.salary-chart-container {
  height: 300px;
}

.job-info-row, .trend-row {
  margin-bottom: 0;
}

.job-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.job-item {
  background: rgba(255, 255, 255, 0.05);
  padding: 15px;
  border-radius: 8px;
  border-left: 3px solid #409EFF;
}

.job-name {
  font-weight: bold;
  margin-bottom: 8px;
  font-size: 14px;
}

.job-stats {
  display: flex;
  gap: 15px;
  margin-bottom: 8px;
  font-size: 12px;
}

.recruit-count {
  color: #409EFF;
}

.applicant-count {
  color: #67C23A;
}

.job-salary {
  color: white;
  font-size: 12px;
  font-weight: bold;
}

:deep(.el-card__body) {
  padding: 20px;
}

:deep(.el-button--primary) {
  background: #409EFF;
  border-color: white;
}

:deep(.el-button--text) {
  color: #409EFF;
}

/* 噪点纹理 */
.noise-texture {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255, 255, 255, 0.05) 35px, rgba(255, 255, 255, 0.05) 70px),
    repeating-linear-gradient(-45deg, transparent, transparent 35px, rgba(255, 255, 255, 0.05) 35px, rgba(255, 255, 255, 0.05) 70px);
  opacity: 0.05;
  z-index: 2;
  pointer-events: none;
}

/* 左侧垂直数据流 */
.data-stream {
  position: absolute;
  left: 0;
  top: 0;
  width: 100px;
  height: 100%;
  z-index: 3;
  pointer-events: none;
}

.data-stream .stream-line {
  position: absolute;
  left: 20px;
  bottom: -100px;
  width: 2px;
  height: 100px;
  background: linear-gradient(to top, transparent, #4096ff, transparent);
  animation: dataFlow 10s linear infinite;
  opacity: 0.6;
}

.data-stream .stream-line:nth-child(2) {
  left: 40px;
  width: 3px;
  animation-delay: -3s;
  animation-duration: 12s;
}

.data-stream .stream-line:nth-child(3) {
  left: 60px;
  width: 1px;
  animation-delay: -6s;
  animation-duration: 8s;
}

/* 右下角六边形装饰 */
.hexagon-decoration {
  position: absolute;
  right: 50px;
  bottom: 50px;
  width: 80px;
  height: 80px;
  z-index: 4;
  pointer-events: none;
  animation: hexagonFloat 20s ease-in-out infinite;
}

.hexagon-decoration::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #00c1d4;
  opacity: 0.15;
  clip-path: polygon(30% 0%, 70% 0%, 100% 50%, 70% 100%, 30% 100%, 0% 50%);
  transform: rotate(15deg);
}

/* 数据流动画 */
@keyframes dataFlow {
  0% {
    transform: translateY(0);
    opacity: 0;
  }
  10% {
    opacity: 0.6;
  }
  90% {
    opacity: 0.6;
  }
  100% {
    transform: translateY(-100vh);
    opacity: 0;
  }
}

/* 六边形浮动动画 */
@keyframes hexagonFloat {
  0%, 100% {
    transform: translateY(0) rotate(15deg);
  }
  25% {
    transform: translateY(-10px) rotate(18deg);
  }
  50% {
    transform: translateY(5px) rotate(12deg);
  }
  75% {
    transform: translateY(-8px) rotate(17deg);
  }
}
</style>